import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { KeyRound, Mail, Sparkles, AlertCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/admin-appointments");
    }
  }, [user, navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/admin-appointments");
    } catch (err) {
      setError(err.message || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <style>{`
        * { box-sizing: border-box; }
        .login-page {
          min-height: 90vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 60px 16px;
          font-family: Arial, sans-serif;
          background:
            radial-gradient(circle at top left, rgba(34, 197, 94, 0.12), transparent 24%),
            radial-gradient(circle at bottom right, rgba(21, 128, 61, 0.10), transparent 22%),
            linear-gradient(180deg, #f7fff8 0%, #ffffff 46%, #f2fbf4 100%);
        }

        .login-card {
          width: 100%;
          max-width: 460px;
          background: rgba(255, 255, 255, 0.90);
          border: 1px solid rgba(15, 23, 42, 0.05);
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
          border-radius: 28px;
          padding: 36px 32px;
        }

        .login-header {
          text-align: center;
          margin-bottom: 28px;
        }

        .login-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(34, 197, 94, 0.10);
          color: #15803d;
          border: 1px solid rgba(34, 197, 94, 0.18);
          padding: 8px 16px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 14px;
        }

        .login-header h1 {
          font-size: 26px;
          font-weight: 900;
          color: #0f172a;
          margin: 0 0 8px;
        }

        .login-header p {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          margin-bottom: 18px;
        }

        .form-label {
          font-size: 14px;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .input-wrap {
          position: relative;
        }

        .input-icon {
          position: absolute;
          top: 50%;
          left: 14px;
          transform: translateY(-50%);
          color: #16a34a;
          width: 18px;
          height: 18px;
        }

        .login-input {
          width: 100%;
          border: 1px solid #dbe5dd;
          border-radius: 16px;
          padding: 14px 16px 14px 46px;
          font-size: 15px;
          color: #0f172a;
          background: #f8fbf8;
          outline: none;
          transition: border-color 0.2s;
        }

        .login-input:focus {
          border-color: #22c55e;
          background: #fff;
        }

        .error-alert {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #b91c1c;
          border-radius: 16px;
          padding: 12px;
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
        }

        .submit-btn {
          width: 100%;
          border: none;
          background: linear-gradient(135deg, #15803d 0%, #16a34a 45%, #22c55e 100%);
          color: #fff;
          padding: 15px;
          border-radius: 16px;
          font-size: 16px;
          font-weight: 900;
          cursor: pointer;
          transition: transform 0.2s, opacity 0.2s;
        }

        .submit-btn:hover {
          transform: translateY(-1px);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .login-page {
            padding: 30px 12px;
            align-items: flex-start;
            padding-top: 40px;
          }
          .login-card {
            padding: 24px 18px;
            border-radius: 20px;
          }
          .login-header h1 {
            font-size: 22px;
          }
        }
      `}</style>

      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <div className="login-badge">
              <Sparkles size={13} />
              ADMIN CONTROL PANEL
            </div>
            <h1>Welcome Back</h1>
            <p>Please enter your credentials to access the admin panel</p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="error-alert">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="input-wrap">
                <Mail className="input-icon" />
                <input
                  type="email"
                  className="login-input"
                  placeholder="admin@elitecare.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-wrap">
                <KeyRound className="input-icon" />
                <input
                  type="password"
                  className="login-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Authenticating..." : "Log In"}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </>
  );
}
